import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { initConsentDefaults, loadGTM } from "../utils/load-gtm";

const BANNER_DELAY_MS = 10000;
const CONSENT_COOKIE = "tu_consent";
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180; // 6 months
export const OPEN_COOKIE_SETTINGS = "tu-open-cookie-settings";

const COPY = {
	en: {
		title: "Privacy preferences",
		body: "We use cookies to recognize visitors, remember preferences, measure campaigns, and analyze traffic. Necessary cookies stay on so the site and inquiry forms keep working.",
		privacy: "Privacy policy",
		accept: "Accept all",
		settings: "Cookie settings",
		panelTitle: "Cookie preferences",
		panelIntro: "Choose which cookies TU may use on this website.",
		necessaryTitle: "Necessary cookies",
		necessaryBody:
			"Required for basic website functions, security, and saving your cookie preferences.",
		always: "Always active",
		analyticsTitle: "Analytics cookies",
		analyticsBody:
			"Help TU understand website traffic, pages visited, and how visitors use the site.",
		allow: "Allow",
		advertisingTitle: "Advertising cookies",
		advertisingBody:
			"Help measure Google Ads performance and support ad measurement and personalization where applicable.",
		save: "Save preferences",
	},
	es: {
		title: "Preferencias de privacidad",
		body: "Usamos cookies para reconocer a los visitantes, recordar preferencias, medir campañas y analizar el tráfico. Las cookies necesarias siguen activas para que el sitio y los formularios de consulta sigan funcionando.",
		privacy: "Política de privacidad",
		accept: "Aceptar todo",
		settings: "Configuración de cookies",
		panelTitle: "Preferencias de cookies",
		panelIntro: "Elija qué cookies puede usar TU en este sitio.",
		necessaryTitle: "Cookies necesarias",
		necessaryBody:
			"Necesarias para las funciones básicas del sitio, la seguridad y para guardar sus preferencias de cookies.",
		always: "Siempre activas",
		analyticsTitle: "Cookies de analítica",
		analyticsBody:
			"Ayudan a TU a entender el tráfico del sitio, las páginas visitadas y cómo los visitantes usan el sitio.",
		allow: "Permitir",
		advertisingTitle: "Cookies de publicidad",
		advertisingBody:
			"Ayudan a medir el rendimiento de Google Ads y a respaldar la medición y personalización de anuncios cuando corresponde.",
		save: "Guardar preferencias",
	},
};

const DEFAULT_PREFS = { analytics: true, advertising: true };

function readCookie(name) {
	const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return match ? decodeURIComponent(match[1]) : "";
}

function parseConsentValue(value) {
	if (value === "accepted" || value === "analytics.advertising") {
		return { analytics: true, advertising: true };
	}
	if (value === "declined" || value === "none") {
		return { analytics: false, advertising: false };
	}
	if (value === "analytics") return { analytics: true, advertising: false };
	if (value === "advertising") return { analytics: false, advertising: true };
	return null;
}

function cookieValueFor(prefs) {
	if (prefs.analytics && prefs.advertising) return "analytics.advertising";
	if (prefs.analytics) return "analytics";
	if (prefs.advertising) return "advertising";
	return "none";
}

/** Optional categories only. The gclid lead cookie is never written or cleared here. */
function readStoredConsent() {
	const fromCookie = parseConsentValue(readCookie(CONSENT_COOKIE));
	if (fromCookie) return { prefs: fromCookie, fromCookie: true };
	if (sessionStorage.getItem("theTags1")) {
		return {
			prefs: { analytics: true, advertising: true },
			fromCookie: false,
		};
	}
	if (sessionStorage.getItem("theTags2")) {
		return {
			prefs: { analytics: false, advertising: false },
			fromCookie: false,
		};
	}
	return null;
}

function rememberSession(prefs) {
	if (prefs.analytics || prefs.advertising) {
		sessionStorage.setItem("theTags1", "tagsAccepted");
		sessionStorage.removeItem("theTags2");
		return;
	}
	sessionStorage.setItem("theTags2", "tagsDeclined");
	sessionStorage.removeItem("theTags1");
}

function writeConsentChoice(prefs) {
	const value = cookieValueFor(prefs);
	document.cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${CONSENT_MAX_AGE}; SameSite=Lax`;
	rememberSession(prefs);
}

/**
 * Next's pages router replaceState's the URL it saw at startup after the
 * middleware check. Restoring the click ID has to happen after that promise,
 * and immediately before gtm.js is inserted, or the Ads tag reads a clean URL.
 */
function whenInitialHistorySettled(router) {
	const pending = router._initialMatchesMiddlewarePromise;
	if (pending && typeof pending.then === "function") return pending;
	return Promise.resolve();
}

/** Put the stored click ID on the document URL so the first Google collect can read it. */
function restoreGclid() {
	const gclid = readCookie("gclid");
	if (!gclid) return;
	const url = new URL(window.location.href);
	if (url.searchParams.get("gclid") === gclid) return;
	url.searchParams.set("gclid", gclid);
	const next = `${url.pathname}${url.search}${url.hash}`;
	window.history.replaceState(window.history.state, "", next);
}

function stripGclidFromUrl() {
	const url = new URL(window.location.href);
	if (!url.searchParams.has("gclid")) return;
	url.searchParams.delete("gclid");
	const next = `${url.pathname}${url.search}${url.hash}`;
	window.history.replaceState(window.history.state, "", next);
}

function consentState(prefs) {
	const ads = prefs.advertising ? "granted" : "denied";
	return {
		ad_storage: ads,
		ad_user_data: ads,
		ad_personalization: ads,
		analytics_storage: prefs.analytics ? "granted" : "denied",
	};
}

function applyConsent(prefs) {
	window.gtag("consent", "update", consentState(prefs));
	if (!prefs.analytics && !prefs.advertising) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ event: "consent_granted" });
}

function loadGtm(router, restoreClickId) {
	whenInitialHistorySettled(router).then(() => {
		if (restoreClickId) restoreGclid();
		loadGTM();
	});
}

function scheduleGtm(router, timeout, restoreClickId) {
	const run = () => loadGtm(router, restoreClickId);
	if (typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(run, { timeout });
	} else {
		window.setTimeout(run, 0);
	}
}

export default function CookieBanner() {
	const router = useRouter();
	const [view, setView] = useState(null);
	const [analytics, setAnalytics] = useState(DEFAULT_PREFS.analytics);
	const [advertising, setAdvertising] = useState(DEFAULT_PREFS.advertising);
	const showTimer = useRef(null);
	const copy = router.pathname.startsWith("/es") ? COPY.es : COPY.en;

	useEffect(() => {
		initConsentDefaults();

		const stored = readStoredConsent();
		if (stored) {
			rememberSession(stored.prefs);
			if (!stored.fromCookie) writeConsentChoice(stored.prefs);
			if (stored.prefs.analytics || stored.prefs.advertising) {
				applyConsent(stored.prefs);
				scheduleGtm(router, 5000, stored.prefs.advertising);
			}
			return;
		}

		showTimer.current = window.setTimeout(() => setView("banner"), BANNER_DELAY_MS);
		return () => window.clearTimeout(showTimer.current);
		// Full-page load only. A later client navigation must not put gclid back.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const openSettings = () => {
			const stored = readStoredConsent();
			const prefs = stored ? stored.prefs : DEFAULT_PREFS;
			setAnalytics(prefs.analytics);
			setAdvertising(prefs.advertising);
			setView("panel");
		};
		window.addEventListener(OPEN_COOKIE_SETTINGS, openSettings);
		return () => window.removeEventListener(OPEN_COOKIE_SETTINGS, openSettings);
	}, []);

	useEffect(() => {
		const strip = () => stripGclidFromUrl();
		router.events.on("routeChangeComplete", strip);
		return () => router.events.off("routeChangeComplete", strip);
	}, [router.events]);

	const commit = (prefs) => {
		window.clearTimeout(showTimer.current);
		writeConsentChoice(prefs);
		applyConsent(prefs);
		if (!prefs.advertising) stripGclidFromUrl();
		if (prefs.analytics || prefs.advertising) {
			scheduleGtm(router, 2000, prefs.advertising);
		}
		setView(null);
	};

	if (!view) return null;

	const privacy = (
		<p className="main-cookies__privacy">
			<a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
				{copy.privacy}
			</a>
		</p>
	);

	if (view === "panel") {
		return (
			<div
				id="main-cookies-id"
				className="main-cookies main-cookies--panel"
				role="dialog"
				aria-labelledby="cookie-banner-title"
				aria-describedby="cookie-banner-desc"
			>
				<p id="cookie-banner-title" className="main-cookies__title">
					{copy.panelTitle}
				</p>
				<p id="cookie-banner-desc" className="main-cookies__text">
					{copy.panelIntro}
				</p>
				<div className="main-cookies__category">
					<p className="main-cookies__category-title">{copy.necessaryTitle}</p>
					<p className="main-cookies__category-body">{copy.necessaryBody}</p>
					<p className="main-cookies__always">{copy.always}</p>
				</div>
				<div className="main-cookies__category">
					<p className="main-cookies__category-title">{copy.analyticsTitle}</p>
					<p className="main-cookies__category-body">{copy.analyticsBody}</p>
					<label className="main-cookies__allow">
						<input
							type="checkbox"
							checked={analytics}
							onChange={(event) => setAnalytics(event.target.checked)}
						/>
						{copy.allow}
					</label>
				</div>
				<div className="main-cookies__category">
					<p className="main-cookies__category-title">{copy.advertisingTitle}</p>
					<p className="main-cookies__category-body">{copy.advertisingBody}</p>
					<label className="main-cookies__allow">
						<input
							type="checkbox"
							checked={advertising}
							onChange={(event) => setAdvertising(event.target.checked)}
						/>
						{copy.allow}
					</label>
				</div>
				{privacy}
				<div className="main-cookies__actions">
					<button
						type="button"
						className="main-cookies__accept"
						onClick={() => commit({ analytics, advertising })}
					>
						{copy.save}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div
			id="main-cookies-id"
			className="main-cookies"
			role="dialog"
			aria-labelledby="cookie-banner-title"
			aria-describedby="cookie-banner-desc"
		>
			<p id="cookie-banner-title" className="main-cookies__title">
				{copy.title}
			</p>
			<p id="cookie-banner-desc" className="main-cookies__text">
				{copy.body}
			</p>
			{privacy}
			<div className="main-cookies__actions">
				<button
					type="button"
					className="main-cookies__accept"
					onClick={() => commit({ analytics: true, advertising: true })}
				>
					{copy.accept}
				</button>
				<button
					type="button"
					className="main-cookies__settings"
					onClick={() => {
						window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS));
					}}
				>
					{copy.settings}
				</button>
			</div>
		</div>
	);
}
